import { useRef, useEffect, useCallback, useMemo } from 'react';
import './DotGrid.css';

function hexToRgb(hex) {
  const m = hex.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  if (!m) return { r: 0, g: 0, b: 0 };
  return {
    r: parseInt(m[1], 16),
    g: parseInt(m[2], 16),
    b: parseInt(m[3], 16)
  };
}

const DotGrid = ({
  dotSize = 4,
  gap = 24,
  baseColor = '#3a1f8c',
  activeColor = '#7c5cff',
  proximity = 150,
  pushRadius = 100,
  pushStrength = 30,
  returnSpeed = 0.1,
  rippleRadius = 300,
  rippleStrength = 2,
  rippleDuration = 1000,
  className = '',
  style
}) => {
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);
  const dotsRef = useRef([]);
  const pointerRef = useRef({ x: -1000, y: -1000 });
  const rafRef = useRef(null);
  const ripplesRef = useRef([]);

  const baseRgb = useMemo(() => hexToRgb(baseColor), [baseColor]);
  const activeRgb = useMemo(() => hexToRgb(activeColor), [activeColor]);

  const buildGrid = useCallback(() => {
    const wrap = wrapperRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;

    const { width, height } = wrap.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.scale(dpr, dpr);

    const cell = dotSize + gap;
    const cols = Math.ceil(width / cell) + 1;
    const rows = Math.ceil(height / cell) + 1;

    const gridW = (cols - 1) * cell;
    const gridH = (rows - 1) * cell;
    const startX = (width - gridW) / 2;
    const startY = (height - gridH) / 2;

    const dots = [];
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        dots.push({
          cx: startX + x * cell,
          cy: startY + y * cell,
          ox: 0, // offset x
          oy: 0  // offset y
        });
      }
    }
    dotsRef.current = dots;
  }, [dotSize, gap]);

  useEffect(() => {
    const proxSq = proximity * proximity;
    const pushSq = pushRadius * pushRadius;

    const draw = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const { width, height } = canvas.getBoundingClientRect();
      ctx.clearRect(0, 0, width, height);

      const { x: px, y: py } = pointerRef.current;
      const radius = dotSize / 2;
      const now = performance.now();

      // 更新涟漪效果
      ripplesRef.current = ripplesRef.current.filter(ripple => {
        const elapsed = now - ripple.startTime;
        return elapsed < rippleDuration;
      });

      for (const dot of dotsRef.current) {
        const dx = dot.cx - px;
        const dy = dot.cy - py;
        const dsq = dx * dx + dy * dy;

        // Push effect
        if (dsq < pushSq && dsq > 0) {
          const dist = Math.sqrt(dsq);
          const force = (1 - dist / pushRadius) * pushStrength;
          const angle = Math.atan2(dy, dx);
          dot.ox += Math.cos(angle) * force;
          dot.oy += Math.sin(angle) * force;
        }

        // 涟漪效果
        for (const ripple of ripplesRef.current) {
          const rdx = dot.cx - ripple.x;
          const rdy = dot.cy - ripple.y;
          const rdist = Math.sqrt(rdx * rdx + rdy * rdy);
          const elapsed = now - ripple.startTime;
          const progress = elapsed / rippleDuration;
          const waveRadius = progress * rippleRadius;
          
          // 波浪传播效果
          if (Math.abs(rdist - waveRadius) < 30) {
            const intensity = (1 - progress) * rippleStrength;
            const waveFactor = Math.sin((rdist - waveRadius) * 0.2) * intensity;
            const angle = Math.atan2(rdy, rdx);
            dot.ox += Math.cos(angle) * waveFactor;
            dot.oy += Math.sin(angle) * waveFactor;
          }
        }

        // Return to original position
        dot.ox *= (1 - returnSpeed);
        dot.oy *= (1 - returnSpeed);

        // Calculate draw position
        const drawX = dot.cx + dot.ox;
        const drawY = dot.cy + dot.oy;

        // Color based on proximity
        let r = baseRgb.r, g = baseRgb.g, b = baseRgb.b;
        if (dsq <= proxSq) {
          const dist = Math.sqrt(dsq);
          const t = 1 - dist / proximity;
          r = Math.round(baseRgb.r + (activeRgb.r - baseRgb.r) * t);
          g = Math.round(baseRgb.g + (activeRgb.g - baseRgb.g) * t);
          b = Math.round(baseRgb.b + (activeRgb.b - baseRgb.b) * t);
        }

        ctx.beginPath();
        ctx.arc(drawX, drawY, radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgb(${r},${g},${b})`;
        ctx.fill();
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    draw();
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [proximity, pushRadius, pushStrength, returnSpeed, rippleRadius, rippleStrength, rippleDuration, baseRgb, activeRgb, dotSize]);

  useEffect(() => {
    buildGrid();
    let ro = null;
    if ('ResizeObserver' in window) {
      ro = new ResizeObserver(buildGrid);
      if (wrapperRef.current) ro.observe(wrapperRef.current);
    } else {
      window.addEventListener('resize', buildGrid);
    }
    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener('resize', buildGrid);
    };
  }, [buildGrid]);

  useEffect(() => {
    const onMove = (e) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      pointerRef.current.x = e.clientX - rect.left;
      pointerRef.current.y = e.clientY - rect.top;
    };

    const onLeave = () => {
      pointerRef.current.x = -1000;
      pointerRef.current.y = -1000;
    };

    const onClick = (e) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickY = e.clientY - rect.top;
      
      // 添加涟漪效果
      ripplesRef.current.push({
        x: clickX,
        y: clickY,
        startTime: performance.now()
      });
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    window.addEventListener('mouseleave', onLeave);
    window.addEventListener('click', onClick);

    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseleave', onLeave);
      window.removeEventListener('click', onClick);
    };
  }, []);

  return (
    <section className={`dot-grid ${className}`} style={style}>
      <div ref={wrapperRef} className="dot-grid__wrap">
        <canvas ref={canvasRef} className="dot-grid__canvas" />
      </div>
    </section>
  );
};

export default DotGrid;
